import { useEffect, useRef, useCallback } from 'react';

export const useDigitalAssetCleanup = () => {
    const uploadedKeysRef = useRef([]);
    const isCleanedUpRef = useRef(false);

    const trackUpload = useCallback((key) => {
        if (key && !uploadedKeysRef.current.includes(key)) {
            uploadedKeysRef.current.push(key);
            // Reset cleanup flag when new uploads are tracked
            isCleanedUpRef.current = false;
        }
    }, []);

    const cleanupTempFiles = useCallback(async (keys = uploadedKeysRef.current) => {
        if (keys.length === 0 || isCleanedUpRef.current) return;

        try {
            // Use sendBeacon for reliable cleanup during page unload
            const data = JSON.stringify({ keys });
            const blob = new Blob([data], { type: 'application/json' });
            const url = `${import.meta.env.VITE_BACKEND_URL}/api/upload/cleanup-temp`;

            // Try sendBeacon first (works during page unload)
            const beaconSent = navigator.sendBeacon(url, blob);

            if (!beaconSent) {
                // Fallback to fetch if sendBeacon fails
                await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                    },
                    body: data,
                    keepalive: true // Important for page unload
                });
            }

            // Clear tracked keys after cleanup
            uploadedKeysRef.current = uploadedKeysRef.current.filter(
                key => !keys.includes(key)
            );

            if (uploadedKeysRef.current.length === 0) {
                isCleanedUpRef.current = true;
            }
        } catch (error) {
            console.error('Cleanup error:', error);
        }
    }, []);

    const clearTracking = useCallback(() => {
        uploadedKeysRef.current = [];
        isCleanedUpRef.current = true;
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (uploadedKeysRef.current.length > 0 && !isCleanedUpRef.current) {
                cleanupTempFiles(uploadedKeysRef.current).catch(console.error);
            }
        };
    }, [cleanupTempFiles]);

    // Cleanup on page unload/refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (uploadedKeysRef.current.length > 0 && !isCleanedUpRef.current) {
                cleanupTempFiles(uploadedKeysRef.current);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [cleanupTempFiles]);

    return { trackUpload, cleanupTempFiles, clearTracking };
};